import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tenantApi,
  EmbeddingItem,
  SimilarContentItem,
} from '../lib/tenant-api';
import { PageLoader, ConfirmationModal, Pagination } from '../components/common';
import { StatCard } from '../components/StatCard';
import { RefreshIcon } from '../components/icons/RefreshIcon';
import { DeleteBinIcon } from '../components/icons/DeleteBinIcon';
import { toast } from 'react-toastify';
import { useTenantType } from '../hooks/useTenantType';
import clsx from 'clsx';
import {
  Database,
  Search,
  Play,
  AlertCircle,
  Loader2,
  Package,
  FileText,
  CopyIcon,
  CheckIcon,
  HelpCircle,
  Code2,
  Heart,
  Type,
  ChevronDown,
} from 'lucide-react';

type SearchMode = 'keyword' | 'vector';

interface DisplayEmbedding {
  id: string;
  content_type: string;
  content_id: string;
  text_content: string;
  model_name: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
  similarity?: number;
}

const CONTENT_TYPE_ORDER = ['service', 'document', 'question', 'product'] as const;

const CONTENT_TYPE_ICON_CONFIG: Record<
  string,
  { bg: string; color: string; icon: React.ComponentType<{ className?: string }> }
> = {
  service: { bg: 'bg-green-100', color: 'text-green-600', icon: Heart },
  document: { bg: 'bg-purple-100', color: 'text-purple-600', icon: FileText },
  question: { bg: 'bg-rose-100', color: 'text-rose-600', icon: HelpCircle },
  product: { bg: 'bg-indigo-100', color: 'text-indigo-600', icon: Package },
};

function ContentTypeIconBadge({
  type,
  size = 'md',
}: {
  type: string;
  size?: 'md' | 'sm';
}) {
  const config = CONTENT_TYPE_ICON_CONFIG[type] ?? {
    bg: 'bg-gray-100',
    color: 'text-gray-600',
    icon: Database,
  };
  const Icon = config.icon;
  const boxSize = size === 'sm' ? 'h-6 w-6 rounded' : 'h-9 w-9 rounded-md';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  return (
    <span className={clsx('inline-flex items-center justify-center shrink-0', boxSize, config.bg)}>
      <Icon className={clsx(iconSize, config.color)} />
    </span>
  );
}

const CONTROL_SURFACE_CLASS =
  'bg-gray-100 hover:bg-gray-200 text-gray-700 border-0 focus:outline-none focus:ring-0';

/** Shared height for the search toolbar row (toggle container, types dropdown, refresh). */
const EMBEDDINGS_TOOLBAR_HEIGHT = 'h-14';

function truncateId(value: string, length = 8): string {
  if (value.length <= length) return value;
  return `${value.substring(0, length)}...`;
}

const EmbeddingsManagement = () => {
  const queryClient = useQueryClient();
  const { tenantType } = useTenantType();

  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [searchMode, setSearchMode] = useState<SearchMode>('keyword');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [embeddingsFilter, setEmbeddingsFilter] = useState('');
  const [embeddingsPage, setEmbeddingsPage] = useState(0);
  const [embeddingsLimit] = useState(6);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [regenerateAllModalOpen, setRegenerateAllModalOpen] = useState(false);
  const [embeddingToDelete, setEmbeddingToDelete] = useState<EmbeddingItem | null>(null);
  const [vectorResults, setVectorResults] = useState<SimilarContentItem[] | null>(null);
  const [isVectorSearching, setIsVectorSearching] = useState(false);

  const getFilteredContentTypes = (contentTypes: string[]) =>
    contentTypes.filter((type) => {
      if (tenantType === 'product') return type !== 'service';
      if (tenantType === 'service') return type !== 'product';
      return true;
    });

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['embedding-stats'],
    queryFn: tenantApi.getEmbeddingStats,
    refetchInterval: 30000,
  });

  const isVectorModeActive = searchMode === 'vector' && activeSearchQuery.trim().length > 0;

  const { data: embeddingsData, isLoading: embeddingsLoading, error: embeddingsError } = useQuery({
    queryKey: ['embeddings-list', embeddingsFilter, embeddingsPage, embeddingsLimit, activeSearchQuery, searchMode],
    queryFn: () =>
      tenantApi.listEmbeddings(
        embeddingsFilter || undefined,
        embeddingsLimit,
        embeddingsPage * embeddingsLimit,
        searchMode === 'keyword' && activeSearchQuery.trim() ? activeSearchQuery.trim() : undefined
      ),
    enabled: !isVectorModeActive,
    keepPreviousData: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length > 2) {
        setActiveSearchQuery(searchInput);
        setEmbeddingsPage(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setEmbeddingsPage(0);
    if (searchMode === 'vector') {
      setVectorResults(null);
    }
  }, [embeddingsFilter, searchMode]);

  useEffect(() => {
    if (searchMode !== 'vector' || !activeSearchQuery.trim()) {
      setVectorResults(null);
      return;
    }

    let cancelled = false;
    const runVectorSearch = async () => {
      setIsVectorSearching(true);
      try {
        const response = await tenantApi.searchSimilarContent({
          query_text: activeSearchQuery.trim(),
          content_types: embeddingsFilter ? [embeddingsFilter] : undefined,
          limit: 50,
          similarity_threshold: 0.2,
        });
        if (!cancelled) {
          setVectorResults(response.results);
          if (response.results.length === 0) {
            toast.info('No similar content found.');
          }
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Search failed';
          toast.error(`Vector search failed: ${message}`);
          setVectorResults([]);
        }
      } finally {
        if (!cancelled) setIsVectorSearching(false);
      }
    };

    runVectorSearch();
    return () => {
      cancelled = true;
    };
  }, [searchMode, activeSearchQuery, embeddingsFilter]);

  const deleteEmbeddingMutation = useMutation({
    mutationFn: tenantApi.deleteEmbeddingById,
    onSuccess: () => {
      toast.success('Embedding deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['embeddings-list'] });
      queryClient.invalidateQueries({ queryKey: ['embedding-stats'] });
      setDeleteModalOpen(false);
      setEmbeddingToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete embedding: ${error.message}`);
    },
  });

  const regenerateSelectedMutation = useMutation({
    mutationFn: tenantApi.regenerateSelectedEmbeddings,
    onSuccess: () => {
      toast.success('Background regeneration started! This may take a few minutes.');
      queryClient.invalidateQueries({ queryKey: ['embedding-stats'] });
      setSelectedContentTypes([]);
    },
    onError: (error: Error) => {
      toast.error(`Failed to start regeneration: ${error.message}`);
    },
  });

  const regenerateAllMutation = useMutation({
    mutationFn: tenantApi.regenerateAllEmbeddings,
    onSuccess: () => {
      toast.success('Background regeneration started! This may take a few minutes.');
      queryClient.invalidateQueries({ queryKey: ['embedding-stats'] });
      setRegenerateAllModalOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to start regeneration: ${error.message}`);
    },
  });

  const handleRegenerateSelected = () => {
    if (selectedContentTypes.length === 0) {
      toast.warning('Please select at least one content type.');
      return;
    }
    regenerateSelectedMutation.mutate({
      content_types: selectedContentTypes,
      replace_existing: true,
    });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getContentTypeLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Product';
      case 'service':
        return 'Service';
      case 'document':
        return 'Document';
      case 'question':
        return 'Question';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'product':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'service':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'document':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'question':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statCards = useMemo(() => {
    if (!stats) return [];
    const cards = [
      { key: 'total', title: 'Total Embeddings', value: stats.total_embeddings, icon: Code2 },
    ];
    CONTENT_TYPE_ORDER.forEach((type) => {
      if (!getFilteredContentTypes([type]).length) return;
      const icons: Record<string, typeof Heart> = {
        service: Heart,
        document: FileText,
        question: HelpCircle,
        product: Package,
      };
      cards.push({
        key: type,
        title: getContentTypeLabel(type),
        value: stats.by_content_type[type] || 0,
        icon: icons[type] || Database,
      });
    });
    return cards;
  }, [stats, tenantType]);

  const listEmbeddings: DisplayEmbedding[] = useMemo(() => {
    if (isVectorModeActive && vectorResults) {
      return vectorResults.map((result, index) => ({
        id: `${result.content_id}-${index}`,
        content_type: result.content_type,
        content_id: result.content_id,
        text_content: result.text_content,
        model_name: result.model_name,
        similarity: result.similarity,
      }));
    }
    return (embeddingsData?.embeddings || []).map((embedding) => ({
      ...embedding,
    }));
  }, [isVectorModeActive, vectorResults, embeddingsData]);

  const totalEmbeddings = isVectorModeActive
    ? listEmbeddings.length
    : embeddingsData?.total ?? embeddingsData?.total_returned ?? 0;

  const totalPages = Math.max(1, Math.ceil(totalEmbeddings / embeddingsLimit));
  const showPagination = !isVectorModeActive && totalEmbeddings > embeddingsLimit;
  const listLoading = isVectorModeActive ? isVectorSearching : embeddingsLoading;

  if (statsLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Embeddings</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage vector embeddings for AI-powered content search and recommendations
          </p>
        </div>
        <button
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['embedding-stats'] });
            queryClient.invalidateQueries({ queryKey: ['embeddings-list'] });
          }}
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
        >
          <RefreshIcon className="h-4 w-4 mr-2 text-gray-500" />
          Refresh
        </button>
      </div>

      {statsError ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading statistics</h3>
              <p className="mt-1 text-sm text-red-700">Failed to load embedding statistics.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((card) => (
            <StatCard
              key={card.key}
              title={card.title}
              value={card.value}
              icon={card.icon}
              skipTitleCase
              valueAlign="left"
              compact
              dense
              rounded
            />
          ))}
        </div>
      )}

      <div className="bg-white shadow border border-gray-200 rounded-md p-6">
        <h2 className="text-xl font-bold text-gray-900">Generate Embeddings</h2>
        <p className="mt-1 text-sm text-gray-600">
          Generate vector embeddings for your content to enable AI-powered search and recommendations.
        </p>

        <h3 className="mt-6 text-sm font-bold text-gray-900">Context types</h3>

        <div className="mt-3 space-y-3">
          {getFilteredContentTypes(stats?.supported_content_types || []).map((type) => (
            <label
              key={type}
              className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3 hover:bg-gray-50 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <ContentTypeIconBadge type={type} size="sm" />
                <span className="text-sm font-medium text-gray-900">{getContentTypeLabel(type)}</span>
              </div>
              <input
                type="checkbox"
                className="h-4 w-4 appearance-none rounded-full border border-gray-300 bg-white text-indigo-600 checked:border-indigo-600 checked:bg-indigo-600 focus:outline-none focus:ring-0"
                checked={selectedContentTypes.includes(type)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedContentTypes([...selectedContentTypes, type]);
                  } else {
                    setSelectedContentTypes(selectedContentTypes.filter((t) => t !== type));
                  }
                }}
              />
            </label>
          ))}
        </div>

        <div className="mt-6 flex w-full items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleRegenerateSelected}
            disabled={regenerateSelectedMutation.isLoading || selectedContentTypes.length === 0}
            className="inline-flex flex-1 justify-center items-center rounded-md px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed sm:flex-none sm:min-w-[180px]"
          >
            {regenerateSelectedMutation.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Generate selected
          </button>
          <button
            type="button"
            onClick={() => setRegenerateAllModalOpen(true)}
            disabled={regenerateAllMutation.isLoading}
            className={clsx(
              'inline-flex flex-1 justify-center items-center rounded-md px-4 py-2.5 text-sm font-medium text-gray-700 disabled:opacity-50 sm:flex-none sm:min-w-[260px]',
              CONTROL_SURFACE_CLASS
            )}
          >
            <RefreshIcon className="h-4 w-4 mr-2 text-gray-500" />
            Regenerate all embeddings ({stats?.total_embeddings ?? 0})
          </button>
        </div>
      </div>

      <div className="bg-white shadow border border-gray-200 rounded-md">
        <div className="px-4 py-5 sm:px-6 sm:py-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Individual Embeddings</h2>

          <div className="mb-6 flex flex-col lg:flex-row lg:items-center gap-4">
            <div
              className={clsx(
                'flex min-w-0 flex-1 flex-col gap-2 rounded-md bg-gray-50 p-2 lg:flex-row lg:items-center lg:h-14'
              )}
            >
              <div className="inline-flex shrink-0 self-start rounded-md bg-white p-1">
                <button
                  type="button"
                  onClick={() => setSearchMode('keyword')}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded px-3 py-2 text-xs font-medium transition-colors',
                    searchMode === 'keyword'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Type className="h-3.5 w-3.5" />
                  Keyword search
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode('vector')}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded px-3 py-2 text-xs font-medium transition-colors',
                    searchMode === 'vector'
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Search className="h-3.5 w-3.5" />
                  Vector search
                </button>
              </div>

              <div className="hidden lg:block w-px self-stretch bg-gray-200" />
              <div className="border-t border-gray-200 lg:hidden" />

              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={
                    searchMode === 'keyword'
                      ? 'Search embeddings by keywords'
                      : 'Search embeddings by vector similarity'
                  }
                  className="w-full rounded-md border-0 bg-gray-50 py-2 pl-9 pr-4 text-xs font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>

            <div className="relative w-full shrink-0 lg:w-44">
              <select
                value={embeddingsFilter}
                onChange={(e) => setEmbeddingsFilter(e.target.value)}
                className={clsx(
                  'w-full appearance-none rounded-md pl-4 pr-10 text-xs',
                  EMBEDDINGS_TOOLBAR_HEIGHT,
                  CONTROL_SURFACE_CLASS
                )}
              >
                <option value="">All Types</option>
                {tenantType !== 'service' && <option value="product">Products</option>}
                {tenantType !== 'product' && <option value="service">Services</option>}
                <option value="document">Documents</option>
                <option value="question">Questions</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
            </div>

            <button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['embeddings-list'] })}
              className={clsx(
                'inline-flex shrink-0 items-center justify-center rounded-md',
                EMBEDDINGS_TOOLBAR_HEIGHT,
                'w-14',
                CONTROL_SURFACE_CLASS
              )}
              title="Refresh list"
            >
              <RefreshIcon className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          {listLoading ? (
            <PageLoader />
          ) : embeddingsError && !isVectorModeActive ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-700">Failed to load embeddings. Please try refreshing.</p>
            </div>
          ) : listEmbeddings.length > 0 ? (
            <>
              <div className="space-y-4">
                {listEmbeddings.map((embedding) => {
                  const isExpanded = expandedIds.has(embedding.id);
                  const shouldTruncate = embedding.text_content.length > 180;
                  const displayText =
                    shouldTruncate && !isExpanded
                      ? `${embedding.text_content.slice(0, 180)}...`
                      : embedding.text_content;

                  return (
                    <div key={embedding.id} className="rounded-md border border-gray-200 p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">
                            {displayText}
                            {shouldTruncate && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(embedding.id)}
                                className="ml-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                              >
                                {isExpanded ? 'See less' : 'See more'}
                              </button>
                            )}
                          </p>

                          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-gray-500">
                            <span className="inline-flex items-center gap-2">
                              <span className="font-medium text-gray-600">Type:</span>
                              <span
                                className={clsx(
                                  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border',
                                  getContentTypeColor(embedding.content_type)
                                )}
                              >
                                {getContentTypeLabel(embedding.content_type)}
                              </span>
                            </span>

                            <span className="inline-flex items-center gap-2">
                              <span className="font-medium text-gray-600">Content ID:</span>
                              <button
                                type="button"
                                onClick={() => handleCopy(embedding.content_id)}
                                className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-gray-700 hover:bg-gray-200"
                                title="Copy content ID"
                              >
                                <span className="font-semibold">{truncateId(embedding.content_id)}</span>
                                {copiedId === embedding.content_id ? (
                                  <CheckIcon className="h-3 w-3 text-green-600" />
                                ) : (
                                  <CopyIcon className="h-3 w-3 text-gray-500" />
                                )}
                              </button>
                            </span>

                            <span className="inline-flex items-center gap-2">
                              <span className="font-medium text-gray-600">Model:</span>
                              <span className="text-gray-700">{embedding.model_name}</span>
                            </span>

                            {embedding.created_at && (
                              <span className="inline-flex items-center gap-2">
                                <span className="font-medium text-gray-600">Created:</span>
                                <span className="text-gray-700">
                                  {new Date(embedding.created_at).toLocaleDateString()}
                                </span>
                              </span>
                            )}

                            {embedding.similarity !== undefined && (
                              <span className="inline-flex items-center gap-2">
                                <span className="font-medium text-gray-600">Similarity:</span>
                                <span className="font-medium text-indigo-700">
                                  {(embedding.similarity * 100).toFixed(1)}%
                                </span>
                              </span>
                            )}
                          </div>
                        </div>

                        {!isVectorModeActive && (
                          <button
                            type="button"
                            onClick={() => {
                              setEmbeddingToDelete(embedding as EmbeddingItem);
                              setDeleteModalOpen(true);
                            }}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                            title="Delete embedding"
                          >
                            <DeleteBinIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {showPagination && (
                <Pagination
                  className="mt-6"
                  page={embeddingsPage}
                  pageBase={0}
                  totalPages={totalPages}
                  onPageChange={setEmbeddingsPage}
                  rangeStart={embeddingsPage * embeddingsLimit + 1}
                  rangeEnd={Math.min((embeddingsPage + 1) * embeddingsLimit, totalEmbeddings)}
                  totalItems={totalEmbeddings}
                  itemLabel="embeddings"
                />
              )}

              {isVectorModeActive && (
                <div className="mt-4 text-sm text-gray-600">
                  Showing {listEmbeddings.length} vector search result
                  {listEmbeddings.length === 1 ? '' : 's'}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Database className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No embeddings found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeSearchQuery
                  ? 'Try adjusting your search or filters.'
                  : embeddingsFilter
                    ? `No embeddings found for ${embeddingsFilter}.`
                    : 'No embeddings have been generated yet.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => embeddingToDelete && deleteEmbeddingMutation.mutate(embeddingToDelete.id)}
        title="Delete Embedding"
        message="Are you sure you want to delete this embedding? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={deleteEmbeddingMutation.isLoading}
      />

      <ConfirmationModal
        isOpen={regenerateAllModalOpen}
        onClose={() => setRegenerateAllModalOpen(false)}
        onConfirm={() => regenerateAllMutation.mutate()}
        title="Regenerate All Embeddings"
        message={`This will regenerate all ${stats?.total_embeddings ?? 0} embeddings in the background. Existing embeddings may be replaced. Continue?`}
        confirmText="Regenerate all"
        cancelText="Cancel"
        type="warning"
        isLoading={regenerateAllMutation.isLoading}
      />
    </div>
  );
};

export default EmbeddingsManagement;
